import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Segment } from "@spoonity/flags-contract";
import {
  deleteSegment,
  getSegment,
  importSegmentCsv,
  listSegmentKeys,
  patchSegmentKeys,
} from "../../api/endpoints";
import { canWrite, useAuth } from "../../auth/AuthContext";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EmptyState } from "../../components/EmptyState";
import { LoadMore } from "../../components/LoadMore";
import { useToast } from "../../components/Toast";
import { useWorkspaceEnv } from "../../state/WorkspaceEnvContext";

function parseKeys(text: string): string[] {
  return text
    .split(/[\r\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function SegmentDetailPage() {
  const { name = "" } = useParams();
  const { environment } = useWorkspaceEnv();
  const { identity } = useAuth();
  const { pushError, push } = useToast();
  const navigate = useNavigate();
  const writable = canWrite(identity?.role);

  const [segment, setSegment] = useState<Segment | null>(null);
  const [keys, setKeys] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [addText, setAddText] = useState("");
  const [removeText, setRemoveText] = useState("");
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function loadAll() {
    if (!environment) return;
    setLoading(true);
    try {
      const [seg, page] = await Promise.all([
        getSegment(environment.id, name),
        listSegmentKeys(environment.id, name, { limit: 100 }),
      ]);
      setSegment(seg);
      setKeys(page.items);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load segment");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.id, name]);

  async function loadMoreKeys() {
    if (!environment || !nextCursor) return;
    setLoading(true);
    try {
      const page = await listSegmentKeys(environment.id, name, { cursor: nextCursor, limit: 100 });
      setKeys((cur) => [...cur, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load keys");
    } finally {
      setLoading(false);
    }
  }

  async function applyAdd() {
    if (!environment) return;
    const add = parseKeys(addText);
    if (add.length === 0) return;
    setBusy(true);
    try {
      const seg = await patchSegmentKeys(environment.id, name, { add });
      setSegment(seg);
      setAddText("");
      push({ kind: "success", title: `Added ${add.length} key(s)` });
      await loadAll();
    } catch (err) {
      pushError(err, "Could not add keys");
    } finally {
      setBusy(false);
    }
  }

  async function applyRemove() {
    if (!environment) return;
    const remove = parseKeys(removeText);
    if (remove.length === 0) return;
    setBusy(true);
    try {
      const seg = await patchSegmentKeys(environment.id, name, { remove });
      setSegment(seg);
      setRemoveText("");
      push({ kind: "success", title: `Removed ${remove.length} key(s)` });
      await loadAll();
    } catch (err) {
      pushError(err, "Could not remove keys");
    } finally {
      setBusy(false);
    }
  }

  async function applyCsvImport(text: string) {
    if (!environment || !text.trim()) return;
    setBusy(true);
    try {
      const seg = await importSegmentCsv(environment.id, name, text);
      setSegment(seg);
      setCsvText("");
      push({ kind: "success", title: "CSV imported" });
      await loadAll();
    } catch (err) {
      pushError(err, "Could not import CSV");
    } finally {
      setBusy(false);
    }
  }

  async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    await applyCsvImport(text);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function doDelete() {
    if (!environment) return;
    try {
      await deleteSegment(environment.id, name);
      push({ kind: "success", title: `Segment "${name}" deleted` });
      navigate("/segments");
    } catch (err) {
      pushError(err, "Could not delete segment");
    } finally {
      setConfirmDelete(false);
    }
  }

  if (!environment) return null;
  if (loading && !segment) return <p className="muted">Loading…</p>;
  if (!segment) return <EmptyState title="Segment not found" />;

  return (
    <div>
      <div className="row between">
        <div>
          <h1>{segment.name}</h1>
          <p className="muted">{segment.description}</p>
        </div>
        {writable && (
          <button className="btn danger" onClick={() => setConfirmDelete(true)}>
            Delete segment
          </button>
        )}
      </div>
      <p>
        <strong>{segment.keyCount}</strong> keys · change #{segment.changeNumber}
      </p>

      {writable && (
        <div className="card">
          <h3>Add / remove keys</h3>
          <div className="row wrap" style={{ alignItems: "flex-start" }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="add-keys">Add (one per line or comma-separated)</label>
              <textarea
                id="add-keys"
                rows={4}
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
              />
              <button className="btn small" onClick={applyAdd} disabled={busy || !addText.trim()}>
                Add keys
              </button>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="remove-keys">Remove (one per line or comma-separated)</label>
              <textarea
                id="remove-keys"
                rows={4}
                value={removeText}
                onChange={(e) => setRemoveText(e.target.value)}
              />
              <button className="btn small danger" onClick={applyRemove} disabled={busy || !removeText.trim()}>
                Remove keys
              </button>
            </div>
          </div>

          <h3>CSV import</h3>
          <p className="hint">One key per line, optional `key` header — replaces nothing, only adds.</p>
          <div className="field">
            <textarea
              rows={4}
              placeholder={"key\nmember:0001\nmember:0002"}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              aria-label="CSV paste"
            />
          </div>
          <div className="row">
            <button className="btn small" onClick={() => applyCsvImport(csvText)} disabled={busy || !csvText.trim()}>
              Import pasted CSV
            </button>
            <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={onFileChosen} />
          </div>
        </div>
      )}

      <h3>Keys</h3>
      {keys.length === 0 ? (
        <EmptyState title="No keys in this segment" />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Key</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k}>
                <td>{k}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <LoadMore nextCursor={nextCursor} loading={loading} onLoadMore={loadMoreKeys} />

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this segment?"
          message='Rules using "in_segment"/"not_in_segment" on this segment will fail to save until removed.'
          confirmLabel="Delete"
          danger
          onConfirm={doDelete}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
